import type {PullRequestMergeRequirementsPayload, StackEntryPayload} from '../../../page-data/payloads/merge-box'
import type {StatusChecksPageData} from '../../../page-data/payloads/status-checks'
import {hasFailingRequiredChecks, hasPendingRequiredChecks} from '../../mergeability-status'
import {EffectiveEntryStatus} from '../../effective-entry-status'
import type {StackSectionStackEntry} from '../stack-section-types'
import {hydrateMergeBoxStackEntriesWithStackMergeabilityData} from './hydrate-merge-box-stack-entries-with-stack-mergeability-data'
import type {StackMergeabilityPageData} from '../../../page-data/payloads/stack-mergeability'
import type {UseQueryResult} from '@github-ui/react-query'

export type StackEntriesContext = {
  mergeRequirements: PullRequestMergeRequirementsPayload | null
  isRebasing: boolean
  queries: Array<UseQueryResult<StackMergeabilityPageData>>
  // Status checks for the CURRENT pull request being viewed.
  statusChecks?: StatusChecksPageData
}

// Statuses that merging propagation leaves untouched on BEFORE entries.
const MERGING_IGNORED_STATUSES = new Set<EffectiveEntryStatus>([
  EffectiveEntryStatus.Closed,
  EffectiveEntryStatus.Merged,
  EffectiveEntryStatus.Merging,
  EffectiveEntryStatus.Queued,
])

// Statuses that are static and shouldn't be changed
const STATIC_STATUSES = new Set<EffectiveEntryStatus>([
  EffectiveEntryStatus.Closed,
  EffectiveEntryStatus.Draft,
  EffectiveEntryStatus.Merged,
  EffectiveEntryStatus.Merging,
  EffectiveEntryStatus.Queued,
])

function allRequiredChecksPassed(statusChecks: StatusChecksPageData | undefined): boolean {
  return !hasFailingRequiredChecks(statusChecks) && !hasPendingRequiredChecks(statusChecks)
}

// Returns a refined effectiveEntryStatus for the CURRENT stack entry
function refineCurrentEntryStatus(
  entry: StackSectionStackEntry,
  {mergeRequirements, statusChecks}: StackEntriesContext,
): EffectiveEntryStatus | undefined {
  // Never override a terminal/static status: a closed, draft, queued, merging,
  // or merged stack entry effective status should stay as-is
  // regardless of the stack's merge requirements state.
  if (entry.effectiveEntryStatus && STATIC_STATUSES.has(entry.effectiveEntryStatus)) {
    return entry.effectiveEntryStatus
  }

  const state = mergeRequirements?.state

  if (mergeRequirements === null || state === 'UNKNOWN') return EffectiveEntryStatus.Loading

  if (state === 'UNMERGEABLE') {
    return EffectiveEntryStatus.NotReady
  }

  if (state === 'MERGEABLE_IF_STATUSES_PASS') {
    // A MERGEABLE_IF_STATUSES_PASS mergeability state is only `Ready` once its required checks
    // pass. Treat missing status-check data as `NotReady`. As the data hasn't loaded,
    // we can't confirm the checks pass.
    if (statusChecks === undefined) {
      return EffectiveEntryStatus.NotReady
    }

    return allRequiredChecksPassed(statusChecks) ? EffectiveEntryStatus.Ready : EffectiveEntryStatus.NotReady
  }

  // A `MERGEABLE` mergeability state on the current entry should always return `Ready`
  // at this point in the refining stage.
  if (state === 'MERGEABLE') {
    return EffectiveEntryStatus.Ready
  }

  return entry.effectiveEntryStatus
}

/**
 * Builds the list of stack entries with their effective statuses in three passes.
 *
 * 1. Hydrate pass: turn each StackEntryPayload into a StackSectionStackEntry.
 * 2. Forward pass: refine the CURRENT entry's status and note if any entry is Merging.
 * 3. Reverse pass: apply merging propagation, not-ready propagation, and the
 *    rebase overlay together.
 *
 * The reverse pass can apply the `BlockedDownstack`, `Merging` or `Loading` effective entry statuses at once because:
 *   - `Merging` status propagation for an entry only needs that entry's position and
 *     status plus the shared `anyMerging` variable set.
 *   - `BlockedDownstack` status propagation is the only step that cares about order, and it reads
 *     the status after merging, so doing merging first ensures order correctness.
 *   - The rebase overlay just remaps one entry at a time, so it runs last without
 *     changing `priorBlocked`.
 *
 * ```text
 *   ┌─────────────────────────┐   ┌──────────────────────────────┐
 *   │  entries                │   │  context.queries             │
 *   │  (merge_box API)        │   │  (stack_mergeability API)    │
 *   └────────────┬────────────┘   └───────────────┬──────────────┘
 *                │                                │
 *                └─ index (entry[i] ↔ query[i]) ──┘
 *                               │
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 1 — HYDRATE                                           ║
 *   ║   StackEntryPayload ──► StackSectionStackEntry             ║
 *   ║   merge .channels + .statusCheckChannels ► AliveChannels   ║
 *   ║   seed effectiveEntryStatus                                ║
 *   ║     (static wins, else query, else placeholder)            ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │ hydratedStackEntries
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 2 — FORWARD  (i = 0 ─► n-1)                           ║
 *   ║   CURRENT entry ► refineCurrentEntryStatus                 ║
 *   ║     (mergeRequirements, statusChecks)                      ║
 *   ║   track anyMerging                                         ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │ refinedStackEntries + anyMerging
 *                               ▼
 *   ╔════════════════════════════════════════════════════════════╗
 *   ║ PASS 3 — REVERSE  (i = n-1 ─► 0, priorBlocked = false)     ║
 *   ║   1. propagateMerging:                                     ║
 *   ║       anyMerging & BEFORE & !ignored ► Merging             ║
 *   ║   2. propagateBlockedDownstack (skips AFTER):              ║
 *   ║       Ready + priorBlocked ► BlockedDownstack              ║
 *   ║       NotReady | Draft | Closed ► priorBlocked = true      ║
 *   ║   3. overlayRebaseState:                                   ║
 *   ║       isRebasing & (Ready|NotReady|BlockedDownstack) ► Loading      ║
 *   ╚════════════════════════════════════════════════════════════╝
 *                               │
 *                               ▼
 *            currentStackEntries: StackSectionStackEntry[]
 *
 *   Stack layout walked in the reverse pass (top ─► base):
 *
 *     index   position
 *      n-1     AFTER    ┐ start (AFTER skips downstack-blocking logic)
 *       k     CURRENT   │ refined in Pass 2
 *       …     BEFORE    │ Merging propagates here…
 *       0     BEFORE    ┘ …NotReady/Draft/Closed blocks UPWARD via priorBlocked
 * ```
 */
export function buildCurrentStackEntries(
  entries: StackEntryPayload[],
  context: StackEntriesContext,
): StackSectionStackEntry[] {
  // Hydrate stack entries by combining stack entries from the
  // `merge_box` API data and `stack_mergeability` API data (when it exists).
  // This is our initial source of truth before setting dynamic effectiveEntryStatuses
  // that are contextually aware of sibling StackEntries.
  const hydratedStackEntries = hydrateMergeBoxStackEntriesWithStackMergeabilityData(context.queries)(entries)

  // Forward pass: refine the CURRENT entry and detect whether anything is Merging.
  let anyMerging = false
  const refinedStackEntries: StackSectionStackEntry[] = new Array(hydratedStackEntries.length)
  for (let i = 0; i < hydratedStackEntries.length; i++) {
    const entry = hydratedStackEntries[i]!
    let next = entry

    if (entry.position === 'CURRENT') {
      const status = refineCurrentEntryStatus(entry, context)
      if (status !== entry.effectiveEntryStatus) next = {...entry, effectiveEntryStatus: status}
    }

    if (next.effectiveEntryStatus === EffectiveEntryStatus.Merging) anyMerging = true
    refinedStackEntries[i] = next
  }

  // Reverse pass: merging propagation + not-ready propagation + rebase overlay.
  const currentStackEntries: StackSectionStackEntry[] = new Array(refinedStackEntries.length)
  let priorBlocked = false
  for (let i = refinedStackEntries.length - 1; i >= 0; i--) {
    const entry = refinedStackEntries[i]!
    let status = entry.effectiveEntryStatus

    // propagateMerging: a BEFORE entry becomes Merging when any entry is Merging,
    // unless it's in a terminal/ignored state.
    if (anyMerging && entry.position === 'BEFORE' && !(status && MERGING_IGNORED_STATUSES.has(status))) {
      status = EffectiveEntryStatus.Merging
    }

    // propagateBlockedDownstack: AFTER entries don't participate. A Ready entry
    // that sits above a downstack NotReady, Draft, or Closed entry can't merge
    // yet, so it surfaces as BlockedDownstack (it would be Ready on its own). A
    // truly NotReady entry stays NotReady, while Draft and Closed entries keep
    // their own status and still block everything stacked above them.
    if (entry.position !== 'AFTER') {
      if (priorBlocked && status === EffectiveEntryStatus.Ready) {
        status = EffectiveEntryStatus.BlockedDownstack
      }

      if (
        status === EffectiveEntryStatus.NotReady ||
        status === EffectiveEntryStatus.Draft ||
        status === EffectiveEntryStatus.Closed
      ) {
        priorBlocked = true
      }
    }

    // overlayRebaseState: while rebasing, Ready/NotReady/BlockedDownstack render as Loading.
    if (
      context.isRebasing &&
      (status === EffectiveEntryStatus.Ready ||
        status === EffectiveEntryStatus.NotReady ||
        status === EffectiveEntryStatus.BlockedDownstack)
    ) {
      status = EffectiveEntryStatus.Loading
    }

    currentStackEntries[i] = status === entry.effectiveEntryStatus ? entry : {...entry, effectiveEntryStatus: status}
  }

  return currentStackEntries
}
